import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Sun, Sunset, Sparkles, Map as MapIcon, LayoutGrid, Locate } from "lucide-react";
import PoolCard from "@/components/PoolCard";

// Marker icon: custom pin
const makeIcon = (label) => L.divIcon({
  html: `<div style="background:#005C7A;color:#fff;padding:4px 10px;border-radius:16px;box-shadow:0 4px 12px rgba(0,92,122,0.35);font-weight:700;font-size:12px;font-family:Manrope,sans-serif;white-space:nowrap;transform:translate(-50%,-100%);border:2px solid #fff">${label}</div>`,
  className: "",
  iconSize: [0, 0],
});

function FitBounds({ pools, userLocation }) {
  const map = useMap();
  useEffect(() => {
    if (userLocation) {
      map.setView(userLocation, 10);
      return;
    }
    const withCoords = pools.filter((p) => p.lat && p.lng);
    if (withCoords.length === 0) {
      // Fallback: wide view of Northern Italy
      map.setView([45.5, 10.5], 7);
      return;
    }
    if (withCoords.length === 1) { map.setView([withCoords[0].lat, withCoords[0].lng], 10); return; }
    const bounds = L.latLngBounds(withCoords.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [pools, userLocation, map]);
  return null;
}

function computeSlotPrices(pool) {
  const full = pool.price_per_day;
  const halfPct = pool.half_day_price_pct ?? 0.6;
  const halfBase = +(full * halfPct).toFixed(2);
  const fullDisc = pool.full_day_discount_price;
  const halfDisc = pool.half_day_discount_price;
  return {
    full: {
      current: fullDisc && fullDisc > 0 ? fullDisc : full,
      original: full,
      onSale: !!(fullDisc && fullDisc > 0 && fullDisc < full),
    },
    half: {
      current: halfDisc && halfDisc > 0 ? halfDisc : halfBase,
      original: halfBase,
      onSale: !!(halfDisc && halfDisc > 0 && halfDisc < halfBase),
    },
  };
}

function PriceRow({ label, icon: Icon, iconColor, priceInfo }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      <span className="text-muted-foreground">{label}</span>
      {priceInfo.onSale ? (
        <span className="ml-auto">
          <span className="line-through text-muted-foreground text-xs mr-1">€{priceInfo.original}</span>
          <span className="font-bold text-[#E27B58]">€{priceInfo.current}</span>
        </span>
      ) : (
        <span className="ml-auto font-bold">€{priceInfo.current}</span>
      )}
    </div>
  );
}

export default function MapSearch() {
  const { t } = useApp();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotFilter, setSlotFilter] = useState("all"); // all|full|half|offer
  const [view, setView] = useState("map"); // map|grid
  const [userLocation, setUserLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle|granted|denied

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation([pos.coords.latitude, pos.coords.longitude]); setGeoStatus("granted"); },
      () => { setGeoStatus("denied"); },
      { timeout: 8000, maximumAge: 600000 },
    );
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/pools").then((r) => { if (alive) { setPools(r.data); setLoading(false); } });
    load();
    const iv = setInterval(load, 20000); // auto-refresh so owner changes propagate
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const filtered = useMemo(() => {
    return pools.filter((p) => {
      if (slotFilter === "full" && p.full_day_discount_price === undefined && !p.price_per_day) return false;
      if (slotFilter === "half") {
        const half = p.half_day_discount_price || (p.price_per_day * (p.half_day_price_pct ?? 0.6));
        if (!half) return false;
      }
      if (slotFilter === "offer" && !p.daily_offer) return false;
      return true;
    });
  }, [pools, slotFilter]);

  const withCoords = filtered.filter((p) => p.lat && p.lng);
  const center = withCoords.length ? [withCoords[0].lat, withCoords[0].lng] : [42.5, 12.5];

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-[#E27B58] mb-2">{t("map.badge")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold">{t("map.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 border ${view === "map" ? "bg-[#005C7A] text-white border-[#005C7A]" : "border-border bg-white"}`} data-testid="view-map"><MapIcon className="w-3.5 h-3.5" /> {t("map.viewMap")}</button>
          <button onClick={() => setView("grid")} className={`px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 border ${view === "grid" ? "bg-[#005C7A] text-white border-[#005C7A]" : "border-border bg-white"}`} data-testid="view-grid"><LayoutGrid className="w-3.5 h-3.5" /> {t("map.viewGrid")}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="bg-white border border-border rounded-2xl p-5 h-fit lg:sticky lg:top-24">
          <div className="text-sm font-semibold mb-3">{t("map.filterAccess")}</div>
          <div className="grid gap-2">
            {[
              { k: "all", label: t("map.allAccess"), Icon: Sparkles, color: "text-[#005C7A]" },
              { k: "full", label: t("pool.slotFull"), Icon: Sun, color: "text-[#005C7A]" },
              { k: "half", label: t("pool.slotHalf"), Icon: Sunset, color: "text-[#E27B58]" },
              { k: "offer", label: t("map.dailyOffers"), Icon: Sparkles, color: "text-green-600" },
            ].map((o) => (
              <button key={o.k} onClick={() => setSlotFilter(o.k)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm border-2 transition ${slotFilter === o.k ? "border-[#005C7A] bg-[#005C7A]/5 font-semibold" : "border-border hover:border-[#005C7A]/40"}`}
                data-testid={`filter-${o.k}`}>
                <o.Icon className={`w-4 h-4 ${o.color}`} />
                <span>{o.label}</span>
              </button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-4">{filtered.length} {t("search.results")}</div>
        </aside>

        <div>
          {view === "map" ? (
            <div className="bg-white border border-border rounded-2xl overflow-hidden h-[70vh] min-h-[560px]" data-testid="map-container">
              {loading ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">Loading map...</div>
              ) : (
                <MapContainer center={userLocation || [45.5, 10.5]} zoom={userLocation ? 10 : 7} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <FitBounds pools={withCoords} userLocation={userLocation} />
                  {userLocation && (
                    <Marker position={userLocation} icon={L.divIcon({ html: `<div style="width:16px;height:16px;background:#3B82F6;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 4px rgba(59,130,246,0.3);transform:translate(-50%,-50%)"></div>`, className: "", iconSize: [0, 0] })}>
                      <Popup>Sei qui</Popup>
                    </Marker>
                  )}
                  {withCoords.map((p) => {
                    const prices = computeSlotPrices(p);
                    const label = `€${prices.full.current}${prices.full.onSale ? "🔥" : ""}`;
                    return (
                      <Marker key={p.pool_id} position={[p.lat, p.lng]} icon={makeIcon(label)}>
                        <Popup minWidth={280} maxWidth={320}>
                          <div className="font-body" data-testid={`popup-${p.pool_id}`}>
                            {p.photos?.[0] && (
                              <img src={fileUrl(p.photos[0])} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />
                            )}
                            <div className="font-display text-base font-bold leading-tight mb-0.5">{p.title}</div>
                            <div className="text-xs text-muted-foreground mb-2">{p.hotel_name} · {p.city}</div>
                            {p.daily_offer && (
                              <div className="bg-green-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg mb-2 flex items-center gap-1.5 shadow-sm" data-testid={`offer-${p.pool_id}`}>
                                <Sparkles className="w-3.5 h-3.5" /> {p.daily_offer}
                              </div>
                            )}
                            <div className="space-y-1.5 mb-3 pt-1 border-t border-border">
                              <PriceRow label={t("pool.slotFull")} icon={Sun} iconColor="text-[#005C7A]" priceInfo={prices.full} />
                              <PriceRow label={t("pool.slotHalf")} icon={Sunset} iconColor="text-[#E27B58]" priceInfo={prices.half} />
                            </div>
                            {p.amenities?.length > 0 && (
                              <div className="mb-3 pt-2 border-t border-border">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Servizi</div>
                                <div className="flex flex-wrap gap-1">
                                  {p.amenities.map((a) => (
                                    <span key={a} className="text-[10px] px-2 py-0.5 bg-[#005C7A]/8 text-[#005C7A] rounded-full">{t(`amenity.${a}`)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <Link to={`/pool/${p.pool_id}`}>
                              <Button size="sm" className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid={`popup-cta-${p.pool_id}`}>
                                {t("map.viewDetails")}
                              </Button>
                            </Link>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((p) => <PoolCard key={p.pool_id} pool={p} />)}
              {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-12">{t("search.empty")}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
